import RuleEngine from '@cybusio/rule-engine'

import { Cache, EventListener } from '../../utils'

import { CybusRuleEngineExecutionError } from '../../domain'

import type { RuleEngineService } from '../../application'

/**
 * The rule engine sometimes yields metadata in its outbound objects
 * This is not useful to the user, so they will be stripped
 */
const stripMetadata = (input: unknown): unknown => {
    /**
     * In order to not break the `parse` or `stringify` methods
     * first we wrap the possibly undefined value into an object
     * and then strip it once done reserializing
     */
    const wrapped = { value: input }
    const { value: output } = JSON.parse(JSON.stringify(wrapped)) as typeof wrapped

    return output
}

export class ConnectwareRuleEngineService implements Pick<RuleEngineService, 'transform'> {
    /** Setup cache for successful results */
    private readonly cache = new Cache<Promise<unknown>>()

    /**
     * Only used in tests
     */
    protected readonly warningsListener = new EventListener<readonly unknown[]>()

    transform (input: unknown, transformation: string): Promise<unknown> {
        return this.cache.get(
            () =>
                new Promise<unknown>((resolve, reject) => {
                    void new RuleEngine({
                        configuration: [{ transform: { expression: transformation } }],
                        /** The log is sadly required :') */
                        log: { warn: (...args: readonly unknown[]) => this.warningsListener.trigger(args) },
                        /** Log id is also required :') */
                        id: ConnectwareRuleEngineService.name,
                        /** As soon as an error occurs, just reject everything */
                        onExecutionError: ({ error: [prefix, details], ruleName, rule, message }) =>
                            reject(new CybusRuleEngineExecutionError({ messagePrefix: prefix, messageDetails: details, rule, ruleName, payload: message })),
                    })
                        .run(input)
                        /**
                         * The rule engine will always yield something,
                         * so just wait until everything is done and any errors fire events before resolving
                         */
                        .then((output) => resolve(stripMetadata(output)))
                }),
            input,
            transformation
        )
    }
}
